import type EmberArray from '@ember/array';
import ArrayProxy from '@ember/array/proxy';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import type GroupModel from 'garaje/models/group';
import type LocationModel from 'garaje/models/location';
import type LocationSubscriptionModel from 'garaje/models/location-subscription';
import type SubscriptionModel from 'garaje/models/subscription';
import type NewLocationController from 'garaje/pods/new-location/controller';
import type LocationsService from 'garaje/services/locations';
import type StateService from 'garaje/services/state';
import { hash } from 'rsvp';

interface NewLocationRouteModel {
  deliveriesSubscription: LocationSubscriptionModel | SubscriptionModel | null;
  groups: ArrayProxy<GroupModel>;
  newLocation: LocationModel;
  vrSubscription: LocationSubscriptionModel | SubscriptionModel | null;
  workplaceSubscription: LocationSubscriptionModel | SubscriptionModel | null;
}

export default class NewLocationRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare state: StateService;
  @service declare locations: LocationsService;
  @service declare router: RouterService;
  @service declare store: Store;

  title = 'Add New Location · Envoy';

  beforeModel(): Promise<void> {
    return this.beforeModelTask.perform();
  }

  model(): Promise<NewLocationRouteModel> {
    let newLocation = this._newLocation();
    const { currentCompany, deliveriesSubscription, vrSubscription, workplaceSubscription } = this.state;
    let groups: ArrayProxy<GroupModel> = ArrayProxy.create() as ArrayProxy<GroupModel>;

    if (this.abilities.can('view locations-grouping')) {
      groups = this.store.findAll('group');
    }

    if (!newLocation) {
      newLocation = this.store.createRecord('location', {
        company: currentCompany,
        employeeScheduleEnabled: true,
      });
    }

    return hash({
      deliveriesSubscription,
      groups,
      newLocation,
      vrSubscription,
      workplaceSubscription,
    });
  }

  setupController(controller: NewLocationController, model: NewLocationRouteModel, transition: Transition): void {
    super.setupController(controller, model, transition);
    controller.setupChangeset();
  }

  resetController(controller: NewLocationController, isExiting: boolean, transition: Transition): void {
    super.resetController(controller, isExiting, transition);
    controller.fromPropertyConnection = false;
  }

  // find an existing new location from a previous attempt
  _newLocation(): LocationModel | undefined {
    const locationsContent = this.locations.content as EmberArray<LocationModel> | undefined;
    return locationsContent?.findBy?.('isNew');
  }

  beforeModelTask = dropTask(async () => {
    await this.locations.loadAllTask.perform();

    if (this.abilities.cannot('create locations')) {
      this.router.transitionTo('unauthorized');
    }
  });
}
